/**
 * The Black Market, the shop window's third face (black-market-ui.ts).
 *
 * A back-alley counter rather than a shopfront: the same commerce cards as the
 * Premium shop (commerce.css.ts), under a red work-lamp, with grime and
 * scratches over the plate and a taped sticker hanging off one corner (the
 * wear-and-clutter rule, Janeks 2026-10-14). Decoration is on pseudo-elements
 * with pointer-events none, shrunk or dropped on phones.
 *
 * 44px tap targets; the lamp's flicker only runs when motion is allowed.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below is
 * one template literal and a stray backtick terminates it.
 */
export default `
#shop-ui .bv-bm {
  position: relative; display: flex; flex-direction: column; gap: 10px;
  padding: 2px 2px 8px; min-width: 0; isolation: isolate;
}
#shop-ui .bv-bm::before {
  content: ""; position: absolute; inset: -6px; z-index: -1; pointer-events: none;
  background:
    radial-gradient(ellipse 60% 120px at 18% 0%, rgba(176, 54, 32, 0.22), transparent 70%),
    url("/clutter/grime.webp") center / 520px auto repeat,
    url("/clutter/scratches.webp") right top / 380px auto no-repeat;
  opacity: 0.55; mix-blend-mode: multiply; border-radius: inherit;
}
#shop-ui .bv-bm::after {
  content: ""; position: absolute; top: -18px; right: -14px; width: 96px; height: 96px;
  pointer-events: none; z-index: 2;
  background: url("/clutter/clutter-b.webp") center / contain no-repeat;
  transform: rotate(9deg); opacity: 0.9;
  filter: drop-shadow(0 3px 4px rgba(0, 0, 0, 0.55));
}

/* ── Head: title, wallet, Sell ──────────────────────────────────────────── */
#shop-ui .bv-bm-head { display: flex; flex-direction: column; gap: 6px; padding-right: 72px; }
#shop-ui .bv-bm-head .bv-win-title { position: relative; display: inline-block; }
#shop-ui .bv-bm-head .bv-win-title::before {
  content: ""; position: absolute; left: -10px; top: 50%; width: 7px; height: 7px; border-radius: 50%;
  transform: translateY(-50%); background: var(--rust);
  box-shadow: 0 0 6px 2px rgba(196, 72, 40, 0.65);
}
#shop-ui .bv-bm-headbar { display: flex; align-items: center; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
#shop-ui .bv-bm-wallet { margin: 0; }
#shop-ui .bv-bm-sell-open { min-height: 44px; padding: 0 16px; }

/* ── Views and filters ──────────────────────────────────────────────────── */
#shop-ui .bv-bm-views .bv-bm-view { min-height: 44px; }
#shop-ui .bv-bm-filters { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 6px 12px; }
#shop-ui .bv-bm-chips { display: flex; flex-wrap: wrap; gap: 6px; }
#shop-ui .bv-bm-chip {
  min-height: 44px; padding: 0 12px; border-radius: var(--radius-tape);
  background: rgba(10, 7, 4, 0.55); border: 1px solid var(--line); color: var(--bone-dim);
  font: 0.74rem var(--font-display); letter-spacing: 0.06em; text-transform: uppercase; cursor: pointer;
}
#shop-ui .bv-bm-chip:hover { color: var(--bone); }
#shop-ui .bv-bm-chip-on { color: var(--brass-hot); border-color: var(--brass); background: rgba(40, 26, 12, 0.75); }
#shop-ui .bv-bm-chip:focus-visible, #shop-ui .bv-bm-unit:focus-visible { outline: 2px solid var(--brass-hot); outline-offset: 2px; }

/* ── Cards ──────────────────────────────────────────────────────────────── */
#shop-ui .bv-bm-card { position: relative; }
#shop-ui .bv-bm-card-mine { border-color: var(--brass); }
/* Yours: a strip of brass tape across the card's top-left corner. */
#shop-ui .bv-bm-yours {
  position: absolute; top: -4px; left: -2px; z-index: 2; pointer-events: none;
  padding: 3px 10px 2px; transform: rotate(-4deg);
  background:
    repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.08) 0 2px, transparent 2px 5px),
    linear-gradient(180deg, var(--brass-hot), var(--brass));
  color: #1a1209; font: 700 0.64rem/1.2 var(--font-display); letter-spacing: 0.16em; text-transform: uppercase;
  clip-path: polygon(3% 0, 97% 4%, 100% 50%, 96% 100%, 2% 94%, 0 46%);
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.5));
}
#shop-ui .bv-bm-meta { color: var(--bone-faint); font: 0.72rem/1.3 var(--font-body); }
#shop-ui .bv-bm-card .bv-shop-buy, #shop-ui .bv-bm-cancel { min-height: 44px; min-width: 64px; }
#shop-ui .bv-bm-note { padding: 18px 4px; color: var(--bone-dim); text-align: center; }
#shop-ui .bv-bm-pager { display: flex; align-items: center; justify-content: center; gap: 12px; color: var(--bone-dim); }
#shop-ui .bv-bm-pager .bv-act { min-height: 44px; }

/* ── My sales ───────────────────────────────────────────────────────────── */
#shop-ui .bv-bm-sales { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
#shop-ui .bv-bm-sale {
  display: grid; grid-template-columns: 48px minmax(0, 1fr) auto; align-items: center; gap: 10px; padding: 8px 10px;
}
#shop-ui .bv-bm-sale-art { width: 48px; height: 48px; display: grid; place-items: center; }
#shop-ui .bv-bm-sale-art img { max-width: 48px; max-height: 48px; }
#shop-ui .bv-bm-sale-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
#shop-ui .bv-bm-sale-name { color: var(--bone); font: 0.84rem var(--font-body); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
#shop-ui .bv-bm-sale-money { display: flex; flex-direction: column; align-items: flex-end; gap: 1px; font-size: 0.78rem; }
#shop-ui .bv-bm-fee { color: var(--rust); font: 0.7rem var(--font-technical); }
#shop-ui .bv-bm-net { color: var(--moss); font: 0.74rem var(--font-body); white-space: nowrap; }

/* ── Sell prompt ────────────────────────────────────────────────────────── */
#shop-ui .bv-bm-sell-scrim {
  position: fixed; inset: 0; z-index: 5; display: flex; align-items: center; justify-content: center;
  padding: max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom));
}
#shop-ui .bv-bm-sell {
  width: min(560px, 100%); max-height: calc(100dvh - 32px); overflow: auto;
  display: flex; flex-direction: column; gap: 10px; padding: 16px;
}
#shop-ui .bv-bm-units { display: grid; grid-template-columns: repeat(auto-fill, minmax(112px, 1fr)); gap: 8px; }
#shop-ui .bv-bm-unit {
  display: flex; flex-direction: column; align-items: center; gap: 4px; min-height: 44px; padding: 8px 6px;
  background: rgba(10, 7, 4, 0.55); border: 1px solid var(--line); border-radius: var(--radius-well);
  color: var(--bone); cursor: pointer; text-align: center;
}
#shop-ui .bv-bm-unit img { width: 56px; height: 56px; object-fit: contain; }
#shop-ui .bv-bm-unit-on { border-color: var(--brass-hot); box-shadow: 0 0 0 1px var(--brass-hot) inset, 0 0 10px rgba(214, 160, 70, 0.35); }
#shop-ui .bv-bm-unit-name { font: 0.76rem/1.2 var(--font-body); }
#shop-ui .bv-bm-price { display: flex; flex-direction: column; gap: 6px; padding-top: 4px; border-top: 1px solid var(--line); }
#shop-ui .bv-bm-price-label { color: var(--bone-dim); font: 0.74rem var(--font-display); letter-spacing: 0.08em; text-transform: uppercase; }
#shop-ui .bv-bm-price-row { display: grid; grid-template-columns: minmax(0, 1fr) 96px; gap: 10px; align-items: center; }
#shop-ui .bv-bm-price-row input[type="range"] { width: 100%; min-height: 44px; accent-color: var(--brass-hot); }
#shop-ui .bv-bm-price-row input[type="number"] {
  min-height: 44px; padding: 0 8px; border-radius: var(--radius-tape); border: 1px solid var(--brass);
  background: rgba(10, 7, 4, 0.8); color: var(--brass-hot); font: 700 1rem var(--font-technical);
}
#shop-ui .bv-bm-preview { color: var(--bone); font: 0.86rem var(--font-body); margin: 0; }
#shop-ui .bv-bm-sell-actions { display: flex; justify-content: flex-end; gap: 8px; }
#shop-ui .bv-bm-sell-actions .bv-act { min-height: 44px; padding: 0 18px; }

/* ── The Black Market tab: the gold plate in a row of dark ones ─────────
   Janeks, 2026-10-20: "Add visual anchor to the Black market tab in Shop.
   Animation and glow + make it GOLDen." Gold leaf face, the game's Gold coin
   set into it, a halo that breathes and a sheen that sweeps across now and
   then. Without motion it stays gold and lit, just still. Selectors carry
   .bv-shop-front-bm on top of atelier.css's tab rules so they win on
   specificity, not on file order. */
#shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front.bv-shop-front-bm {
  display: inline-flex; align-items: center; gap: 7px; overflow: hidden; isolation: isolate;
  color: #2a1606; text-shadow: 0 1px 0 rgba(255, 238, 180, 0.55);
  border-color: #8a6420; border-top: 2px solid #ffe08a;
  background:
    radial-gradient(130% 120% at 25% 0%, rgba(255, 250, 215, 0.75), transparent 55%),
    linear-gradient(180deg, #ffe39a 0%, #e8b448 38%, #b67d1e 72%, #7a4f12 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 252, 230, 0.8), inset 0 -2px 0 rgba(90, 52, 10, 0.55),
    0 0 10px rgba(255, 196, 64, 0.45), 0 -2px 14px rgba(255, 176, 40, 0.3);
}
#shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front.bv-shop-front-bm:not(.bv-shop-front-soon):hover {
  color: #1e0f03;
  background:
    radial-gradient(130% 120% at 25% 0%, rgba(255, 252, 228, 0.9), transparent 55%),
    linear-gradient(180deg, #fff0b8 0%, #f2c457 38%, #c48a24 72%, #835614 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 252, 230, 0.9), inset 0 -2px 0 rgba(90, 52, 10, 0.55),
    0 0 16px rgba(255, 204, 80, 0.7), 0 -3px 20px rgba(255, 176, 40, 0.45);
}
#shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front.bv-shop-front-bm.bv-shop-front-on {
  color: #1e0f03; border-color: #a87a26; border-top: 2px solid #fff3c4;
  background:
    radial-gradient(130% 120% at 25% 0%, rgba(255, 253, 235, 0.95), transparent 55%),
    linear-gradient(180deg, #fff3c4 0%, #f5c95c 36%, #cf9228 70%, #8a5a14 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 253, 238, 0.95), inset 0 -2px 0 rgba(90, 52, 10, 0.5),
    0 0 18px rgba(255, 210, 90, 0.75), 0 -4px 24px rgba(255, 180, 40, 0.5);
}
#shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front.bv-shop-front-bm:focus-visible { outline-color: #fff3c4; }
/* The sheen: a bright band parked off the left edge until it sweeps. */
#shop-ui .bv-shop-front-bm::after {
  content: ""; position: absolute; inset: -2px auto -2px 0; width: 45%; z-index: -1;
  background: linear-gradient(100deg, transparent 0%, rgba(255, 255, 240, 0.85) 50%, transparent 100%);
  transform: translateX(-160%) skewX(-18deg); pointer-events: none;
}
#shop-ui .bv-shop-front-bm-coin {
  flex: none; display: inline-grid; place-items: center; width: 22px; height: 22px; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #fff6cf, #d39a2c 60%, #6f4710);
  box-shadow: 0 0 0 1px rgba(90, 52, 10, 0.7), 0 0 8px rgba(255, 214, 102, 0.85);
}
#shop-ui .bv-shop-front-bm-img { width: 18px; height: 18px; object-fit: contain; display: block; }
#shop-ui .bv-shop-front-bm-label { position: relative; }

@media (prefers-reduced-motion: no-preference) {
  #shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front.bv-shop-front-bm { animation: bv-bm-front-breathe 2.6s ease-in-out infinite; }
  #shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front.bv-shop-front-bm:hover { animation-play-state: paused; }
  #shop-ui .bv-shop-front-bm::after { animation: bv-bm-front-sheen 4.2s ease-in-out infinite; }
  #shop-ui .bv-shop-front-bm-img { animation: bv-bm-front-coin 4.2s ease-in-out infinite; }
  @keyframes bv-bm-front-breathe {
    0%, 100% { filter: brightness(1) drop-shadow(0 0 2px rgba(255, 200, 70, 0.35)); }
    50% { filter: brightness(1.12) drop-shadow(0 0 9px rgba(255, 205, 80, 0.85)); }
  }
  @keyframes bv-bm-front-sheen {
    0%, 62% { transform: translateX(-160%) skewX(-18deg); }
    86%, 100% { transform: translateX(330%) skewX(-18deg); }
  }
  /* The coin flips once per sweep, just ahead of the shine reaching it. */
  @keyframes bv-bm-front-coin {
    0%, 52% { transform: rotateY(0deg); }
    68%, 100% { transform: rotateY(360deg); }
  }
}

/* Phones: three tabs share one row, so the coin shrinks and the gap closes. */
@media (max-width: 640px) {
  #shop-ui .bv-shop-plate .bv-shop-fronts .bv-shop-front.bv-shop-front-bm { gap: 4px; justify-content: center; }
  #shop-ui .bv-shop-front-bm-coin { width: 16px; height: 16px; }
  #shop-ui .bv-shop-front-bm-img { width: 13px; height: 13px; }
}
@media (max-width: 380px) {
  #shop-ui .bv-shop-front-bm-coin { display: none; }
}

@media (prefers-reduced-motion: no-preference) {
  #shop-ui .bv-bm-head .bv-win-title::before { animation: bv-bm-lamp 6s steps(1, end) infinite; }
  @keyframes bv-bm-lamp { 0%, 91%, 95%, 100% { opacity: 1; } 93% { opacity: 0.35; } }
}

@media (max-width: 520px) {
  #shop-ui .bv-bm::after { width: 64px; height: 64px; top: -10px; right: -8px; }
  #shop-ui .bv-bm-head { padding-right: 48px; }
  #shop-ui .bv-bm-filters { flex-direction: column; }
  #shop-ui .bv-bm-sale { grid-template-columns: 40px minmax(0, 1fr); }
  #shop-ui .bv-bm-sale-money { grid-column: 2; align-items: flex-start; flex-direction: row; flex-wrap: wrap; gap: 4px 10px; }
}
/* Landscape phones are short, not narrow: the head goes onto one row and the
   sell dialog tightens so its buttons stay on screen at 390px tall. */
@media (max-height: 480px) {
  #shop-ui .bv-bm-head { flex-direction: row; align-items: center; justify-content: space-between; gap: 10px; }
  #shop-ui .bv-bm-head .bv-win-sub { display: none; }
  #shop-ui .bv-bm-headbar { flex-wrap: nowrap; }
  #shop-ui .bv-bm { gap: 6px; }
  #shop-ui .bv-bm-sell { gap: 6px; padding: 10px 14px; }
  #shop-ui .bv-bm-units { grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: 6px; }
  #shop-ui .bv-bm-unit { padding: 4px; gap: 2px; }
  #shop-ui .bv-bm-unit img { width: 36px; height: 36px; }
  #shop-ui .bv-bm-price { gap: 2px; }
  #shop-ui .bv-bm-price p { margin: 0; }
}
@media (max-width: 380px) {
  #shop-ui .bv-bm::after { display: none; }
  #shop-ui .bv-bm-head { padding-right: 0; }
}
`;
